// QueryBuilder.ts
import { FilterBuilder } from './FilterBuilder'
import { quoteIdentifier } from './SqlUtils'
import {ColumnVO, IServerSideGetRowsRequest} from 'ag-grid-community'

export class DuckDBQueryBuilder {
    private tableName: string

    constructor(tableName: string) {
        this.tableName = tableName
    }

    public buildQuery(request: IServerSideGetRowsRequest): string {
        const selectClause = this.createSelect(request)
        const fromClause = `from ${this.tableName}`
        const whereClause = FilterBuilder.createWhereClause(request.filterModel, request.groupKeys, request.rowGroupCols)
        const groupByClause = this.createGroupBy(request)
        const orderByClause = this.createOrderBy(request)
        const limitClause = this.createLimit(request)

        return `
      ${selectClause}
      ${fromClause}
      ${whereClause}
      ${groupByClause}
      ${orderByClause}
      ${limitClause}
    `.trim().replace(/\s+/g, ' ')
    }

    private getFieldName(col: ColumnVO): string {
        return col.field || col.id
    }

    private createSelect(request: IServerSideGetRowsRequest): string {
        // Case 1: We are grouping
        if (this.isDoingGrouping(request)) {
            const cols: string[] = []

            // Select the specific group we are currently viewing
            const currentGroupIndex = request.groupKeys.length
            const currentGroupCol = request.rowGroupCols[currentGroupIndex]

            if (currentGroupCol) {
                cols.push(`${quoteIdentifier(this.getFieldName(currentGroupCol))}`)
            }

            // Calculate Aggregations
            request.valueCols.forEach(valCol => {
                const field = this.getFieldName(valCol)
                cols.push(`${valCol.aggFunc}(${quoteIdentifier(field)}) as "${field}"`)
            })

            return `select ${cols.join(', ')}`
        }

        // Case 2: We are at the leaf level (fetching raw rows)
        return 'select *'
    }

    private createGroupBy(request: IServerSideGetRowsRequest): string {
        if (this.isDoingGrouping(request)) {
            const currentGroupIndex = request.groupKeys.length
            const currentGroupCol = request.rowGroupCols[currentGroupIndex]

            if (currentGroupCol) {
                return `group by ${quoteIdentifier(this.getFieldName(currentGroupCol))}`
            }
        }
        return ''
    }

    private createOrderBy(request: IServerSideGetRowsRequest): string {
        if (!request.sortModel || request.sortModel.length === 0) return ''

        const sorts = request.sortModel.map(s => {
            return `${quoteIdentifier(s.colId)} ${s.sort}`
        })

        return `order by ${sorts.join(', ')}`
    }

    private createLimit(request: IServerSideGetRowsRequest): string {
        if (request.startRow === undefined || request.endRow === undefined) return ''

        const limit = request.endRow - request.startRow
        const offset = request.startRow

        return `limit ${limit} offset ${offset}`
    }

    private isDoingGrouping(request: IServerSideGetRowsRequest): boolean {
        return request.rowGroupCols?.length > 0 && request.groupKeys.length < request.rowGroupCols.length
    }
}